import React, { useState, useEffect } from 'react';
import { Mail, Loader, X, Check } from 'lucide-react';
import { envoiDocumentsApi } from '../utils/api';

/**
 * Bouton « enveloppe » : envoie un document généré à la famille (pièce jointe PDF).
 * @param {string} type  'attestation' | 'convention' | 'contrat_accueil' | 'devis' | 'contrat' | 'documents-modeles'
 * @param {number} [enfantId]
 * @param {number} [refId]  id du devis/contrat (ou enfant pour convention)
 * @param {string} [mois]  'AAAA-MM' (attestation)
 * @param {string} [docs]  clés séparées par virgule (documents-modeles)
 * @param {string} [destinataireDefaut]  e-mail parent pré-rempli
 * @param {string} [label]  texte du bouton (défaut : « Envoyer à la famille »)
 */
export default function EnvoiDocumentButton({ type, enfantId, refId, mois, docs, destinataireDefaut = '', messageDefaut = '', label = 'Envoyer à la famille', compact = false, autoOpen = false }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState(destinataireDefaut);
  const [message, setMessage] = useState(messageDefaut);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');
  const [joindreProcess, setJoindreProcess] = useState(type === 'devis');

  useEffect(() => { if (autoOpen) { setErr(''); setDone(''); setOpen(true); } }, [autoOpen]);

  const envoyer = async () => {
    setBusy(true); setErr('');
    try {
      const { data } = await envoiDocumentsApi.envoyer({ type, enfant_id: enfantId, ref_id: refId, mois, docs, destinataire: to, message, process: type === 'devis' ? joindreProcess : undefined });
      setDone(data.message || 'Document envoyé.');
      setTimeout(() => { setOpen(false); setDone(''); }, 1800);
    } catch (e) {
      setErr(e.response?.data?.error || "Échec de l'envoi.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" onClick={() => { setErr(''); setDone(''); setTo(destinataireDefaut); setMessage(messageDefaut); setJoindreProcess(type === 'devis'); setOpen(true); }}
        className={compact ? 'inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-navy-50 text-navy-400 hover:text-navy-700' : 'btn-secondary'}
        title="Envoyer ce document à la famille par e-mail">
        <Mail size={15} />{compact ? '' : <span className="ml-1.5">{label}</span>}
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => !busy && setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-serif text-lg text-navy-900 flex items-center gap-2"><Mail size={18} className="text-navy-500" /> Envoyer à la famille</h3>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            {done ? (
              <div className="py-6 text-center text-emerald-600 flex flex-col items-center gap-2"><Check size={28} /><span className="text-sm">{done}</span></div>
            ) : (
              <>
                {err && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg px-3 py-2 mb-2">{err}</div>}
                <label className="text-xs text-navy-600 block mb-2">Destinataire <span className="text-navy-300">(vide = e-mail du parent enregistré)</span>
                  <input type="email" className="form-input rounded-xl mt-1 text-sm" value={to} onChange={e => setTo(e.target.value)} placeholder="email@parent.fr" />
                </label>
                <label className="text-xs text-navy-600 block mb-3">Message (optionnel)
                  <textarea rows={3} className="form-input rounded-xl mt-1 text-sm" value={message} onChange={e => setMessage(e.target.value)} placeholder="Laissez vide pour le message par défaut." />
                </label>
                {type === 'devis' && (
                  <label className="flex items-start gap-2 mb-3 text-[13px] text-navy-700 cursor-pointer">
                    <input type="checkbox" checked={joindreProcess} onChange={e => setJoindreProcess(e.target.checked)} className="w-4 h-4 mt-0.5 accent-navy-700" />
                    <span>Envoyer le process <span className="text-navy-400">(joint l'infographie « La suite de votre inscription »)</span></span>
                  </label>
                )}
                <p className="text-[11px] text-navy-400 mb-3">Le document PDF sera joint automatiquement à l'e-mail (depuis contact@lescachettes.fr).</p>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600">Annuler</button>
                  <button onClick={envoyer} disabled={busy} className="btn-primary flex items-center gap-1.5 text-sm">
                    {busy ? <Loader size={14} className="animate-spin" /> : <Mail size={14} />} Envoyer
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
